import { observer } from 'mobx-react-lite';
import Image from 'next/legacy/image';
import { useRouter } from 'next/router';
import { useEffect } from 'react';

import Button from '@/components/common/Button/Button';
import LoadingPage from '@/components/common/LoadingPage/LoadingPage';
import type { GoalBasedQuizRecordModelType } from '@/models/api';
import { useStore } from '@/models/root-store';
import { PLAN_MAP } from '@/utils/api-constants';
import { LoadingState, Mode } from '@/utils/constants';

import MealsPlan from '../MealsPlan/MealsPlan';
import { Questionarie } from '../Questionarie/Questionarie';

type Props = {
  goal: string;
  goalBasedQuiz: GoalBasedQuizRecordModelType;
  setIsLoading: (val: boolean) => void;
};

const GoalBased: React.FC<Props> = observer(
  ({ goal, goalBasedQuiz, setIsLoading }: Props) => {
    const { cartStore, productStore } = useStore();
    const { goalBased, draftCart, cartToUse } = cartStore;
    const { productState } = productStore;
    const router = useRouter();
    const route = router.asPath.split('?')[0];
    const { mode, planId = '' } = router.query;
    const isPlanInCart = cartToUse?.plansLookup[planId as string];
    const planIdToUse = planId || draftCart?.planIdToView;
    const isPlanAddedToDraft = draftCart?.plansLookup[planIdToUse as string];
    const { exerciseKey, getSwapConfig } = goalBased;
    const isLoading = goalBased.packDetailState === LoadingState.LOADING;

    // grab the first plan from the list
    const customerGoal = PLAN_MAP[goal as keyof typeof PLAN_MAP];

    const generatePlan = async () => {
      await goalBased.getPackDetails();
      const generatedPlan = goalBased.packOverview[0];
      if (!generatedPlan) return;
      generatedPlan.setPlanRoute(route || '');
      cartStore.generatePlan(generatedPlan, mode);
    };

    useEffect(() => {
      getSwapConfig();
    }, []);

    useEffect(() => {
      if (customerGoal !== goalBased.goal) {
        goalBased.reset();
        draftCart.setPlanIdToView('');
      }
      goalBased.setSwapCategory('default');
      goalBased.setPreset('goal', customerGoal);
    }, [customerGoal, goalBased.goal]);

    useEffect(() => {
      if (planId) {
        draftCart.setPlanIdToView(planId as string);
      }
      if (mode === Mode.EDIT_CART && isPlanInCart && !isPlanAddedToDraft) {
        cartStore.editPlan(planId, mode);
      }
    }, [mode, planId, router.asPath]);

    if (productState !== LoadingState.DONE) {
      return <LoadingPage />;
    }

    return (
      <div>
        {!isPlanAddedToDraft ? (
          <div>
            <Questionarie
              goalBasedQuiz={goalBasedQuiz}
              exerciseKey={exerciseKey}
            />
            {!goalBased.disabled && (
              <div className="fixed bottom-0 z-10 flex w-full justify-center bg-white px-3 py-6 shadow-3xl">
                <Button
                  theme="primary"
                  className="max-h-[56px] w-10/12 py-2 md:w-[380px] md:px-[120px] md:py-[17px]"
                  disabled={isLoading}
                  onClick={generatePlan}
                >
                  <div className="w-full px-4 py-1 font-interMedium text-base leading-4 md:p-0 md:font-interMedium md:leading-[22px]">
                    {/* if loading I want it to say loading */}
                    {isLoading ? (
                      <>
                        <Image
                          width={32}
                          height={24}
                          src="/assets/icons/checkout/Loading.svg"
                          alt={''}
                          className="mr-3 inline size-5 animate-spin"
                        />
                      </>
                    ) : (
                      'Create My Plan'
                    )}
                  </div>
                </Button>
              </div>
            )}
          </div>
        ) : (
          <MealsPlan
            selectedPlan={isPlanAddedToDraft}
            setIsLoading={setIsLoading}
          />
        )}
      </div>
    );
  },
);

export default GoalBased;
